/**
 * BottomSheetModal - Reusable Bottom Sheet Modal Component
 *
 * Usage:
 * const modal = new BottomSheetModal('modalId', {
 *   onOpen: () => console.log('Modal opened'),
 *   onClose: () => console.log('Modal closed'),
 *   closeOnBackdropClick: true,
 *   enableSwipeToClose: true,
 *   swipeThreshold: 100
 * });
 *
 * modal.open();
 * modal.close();
 * modal.toggle();
 */

/* Guarded because some page loads evaluate this file twice; see the note in
   modal-manager.js. Exported onto window so callers outside this file keep
   resolving the name. */
if (!window.BottomSheetModal) {

class BottomSheetModal {
  constructor(modalId, options = {}) {
    this.modalId = modalId;
    this.options = {
      onOpen: () => {},
      onClose: () => {},
      onBeforeOpen: () => {},
      onBeforeClose: () => {},
      closeOnBackdropClick: true,
      closeOnEscape: true,
      enableSwipeToClose: true,
      swipeThreshold: 100,
      animationDuration: 250,
      preventScroll: true,
      ...options,
    };

    this.isOpen = false;
    this.isAnimating = false;

    // Touch handling
    this.startY = 0;
    this.currentY = 0;
    this.isDragging = false;

    // Get DOM elements
    this.modal = document.getElementById(modalId);
    if (!this.modal) {
      console.warn(`BottomSheetModal: Modal with id "${modalId}" not found yet. Will try to initialize on open.`);
      this.initialized = false;
      return;
    }

    // Move modal to body to escape z-index stacking context
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }

    this.backdrop = this.modal.querySelector('[data-backdrop]');
    this.desktopBackdrop = this.modal.querySelector('[data-desktop-backdrop]');
    this.sheet = this.modal.querySelector('[data-sheet]');
    this.mobileSheet = this.modal.querySelector('[data-mobile-sheet]');
    this.desktopModal = this.modal.querySelector('[data-desktop-backdrop]');
    this.content = this.modal.querySelector('[data-modal-content]');
    this.dragHandle = this.modal.querySelector('[data-drag-handle]');
    this.closeButtons = this.modal.querySelectorAll('[data-close-modal]');

    this.initialized = true;
    this.init();
  }

  /* The markup renders the content twice — once in the mobile sheet, once in the
     desktop panel — so a single querySelector always returns the desktop one.
     Drag-to-close and the scroll guards have to read the pane the viewer is
     actually looking at, otherwise scrollTop is read off an empty container and
     a downward swipe closes the sheet mid-scroll. */
  activeContent() {
    if (!this.modal) return this.content;
    var host = window.matchMedia('(min-width: 768px)').matches
      ? this.modal.querySelector('[data-desktop-content]')
      : this.sheet;
    var pane = host ? host.querySelector('[data-modal-content]') : null;
    return pane || this.content;
  }

  init() {
    // Close button handlers
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    });

    // Backdrop click handler
    if (this.options.closeOnBackdropClick) {
      if (this.backdrop) {
        this.backdrop.addEventListener('click', (e) => {
          // Only close if clicking directly on backdrop
          if (e.target === this.backdrop) {
            this.close();
          }
        });
      }
      if (this.desktopBackdrop) {
        this.desktopBackdrop.addEventListener('click', (e) => {
          // Close if clicking on backdrop (not on content)
          if (e.target === this.desktopBackdrop) {
            this.close();
          }
        });
      }
    }

    // Escape key handler
    if (this.options.closeOnEscape) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    // Touch/Swipe handlers for mobile
    if (this.options.enableSwipeToClose && this.sheet) {
      this.initSwipeHandlers();
    }

    // Prevent content scrolling when not at top
    if (this.content) {
      this.initScrollHandlers();
    }
  }

  initSwipeHandlers() {
    let initialScrollTop = 0;

    // Touch start
    this.sheet.addEventListener(
      'touchstart',
      (e) => {
        if (this.isAnimating) return;

        this.startY = e.touches[0].clientY;
        this.currentY = this.startY;
        this.isDragging = false;
        var startPane = this.activeContent();
        initialScrollTop = startPane ? startPane.scrollTop : 0;
      },
      { passive: true }
    );

    // Touch move
    this.sheet.addEventListener('touchmove', (e) => {
      if (this.isAnimating) return;

      const activePane = this.activeContent();
      const contentScrollTop = activePane ? activePane.scrollTop : 0;

      /* Drag-to-close only from the very top; anywhere else the gesture belongs
         to the scroll container. */
      if (contentScrollTop > 0) {
        return;
      }

      this.currentY = e.touches[0].clientY;
      const diff = this.currentY - this.startY;

      // Only handle downward swipes
      if (diff > 0) {
        this.isDragging = true;
        e.preventDefault();

        // Apply transform with rubber band effect
        const rubberBandFactor = Math.min(diff / 500, 1);
        const translateY = diff * (1 - rubberBandFactor * 0.5);

        if (this.sheet) {
          this.sheet.style.transition = 'none';
          this.sheet.style.transform = `translateY(${translateY}px) translateZ(0)`;
        }

        // Fade backdrop proportionally
        if (this.backdrop) {
          const alpha = Math.max(0, 0.5 - (diff / 400) * 0.5);
          this.backdrop.style.backgroundColor = `rgba(0, 0, 0, ${alpha})`;
        }
      }
    });

    // Touch end
    this.sheet.addEventListener(
      'touchend',
      () => {
        if (this.isAnimating) return;

        const diff = this.currentY - this.startY;
        const endPane = this.activeContent();
        const contentScrollTop = endPane ? endPane.scrollTop : 0;

        if (this.isDragging && diff > this.options.swipeThreshold && contentScrollTop === 0) {
          // Close modal
          this.close();
        } else if (this.isDragging) {
          // Reset position - completely remove inline styles
          if (this.sheet) {
            this.sheet.style.removeProperty('transition');
            this.sheet.style.removeProperty('transform');
          }
          if (this.backdrop) {
            this.backdrop.style.removeProperty('background-color');
          }

          // Force reflow to ensure CSS takes over
          if (this.sheet) {
            void this.sheet.offsetHeight;
          }
        }

        this.isDragging = false;
      },
      { passive: true }
    );
  }

  initScrollHandlers() {
    // Prevent overscroll bounce on iOS — bound on every pane, not just the first.
    this.modal.querySelectorAll('[data-modal-content]').forEach((pane) => {
      pane.addEventListener(
        'touchstart',
        () => {
          const scrolledToTop = pane.scrollTop === 0;
          const scrolledToBottom = pane.scrollTop + pane.clientHeight >= pane.scrollHeight;

          if (scrolledToTop || scrolledToBottom) {
            pane.dataset.canScroll = 'true';
          }
        },
        { passive: true }
      );
    });
  }

  async open() {
    // Try to initialize if not already done
    if (!this.initialized) {
      this.modal = document.getElementById(this.modalId);
      if (!this.modal) {
        console.error(`BottomSheetModal: Modal with id "${this.modalId}" not found in DOM`);
        return;
      }

      // Move modal to body to escape z-index stacking context
      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.backdrop = this.modal.querySelector('[data-backdrop]');
      this.desktopBackdrop = this.modal.querySelector('[data-desktop-backdrop]');
      this.sheet = this.modal.querySelector('[data-sheet]');
      this.mobileSheet = this.modal.querySelector('[data-mobile-sheet]');
      this.desktopModal = this.modal.querySelector('[data-desktop-backdrop]');
      this.content = this.modal.querySelector('[data-modal-content]');
      this.dragHandle = this.modal.querySelector('[data-drag-handle]');
      this.closeButtons = this.modal.querySelectorAll('[data-close-modal]');

      this.initialized = true;
      this.init();
    }

    if (this.isOpen || this.isAnimating) return;

    this.isAnimating = true;

    // Call before open callback
    if (this.options.onBeforeOpen) {
      const result = this.options.onBeforeOpen();
      if (result === false) {
        this.isAnimating = false;
        return;
      }
    }

    // Prevent body scroll
    if (this.options.preventScroll) {
      // Save current scroll position
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      // Prevent scroll on body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';

      // Add touch-action for iOS
      document.documentElement.style.overflow = 'hidden';
    }

    // Simple approach: just show and add active class
    this.modal.classList.remove('hidden');

    // Small delay to ensure display change is processed
    await this.wait(10);

    // Add active class to trigger CSS animations
    this.modal.classList.add('active');

    // Wait for animation to complete
    await this.wait(this.options.animationDuration);

    this.isOpen = true;
    this.isAnimating = false;

    // Call open callback
    if (this.options.onOpen) {
      this.options.onOpen();
    }

    // Dispatch custom event
    this.modal.dispatchEvent(
      new CustomEvent('bottomsheet:open', {
        bubbles: true,
        detail: { modalId: this.modalId },
      })
    );
  }

  async close() {
    if (!this.isOpen || this.isAnimating) return;

    this.isAnimating = true;

    // Call before close callback
    if (this.options.onBeforeClose) {
      const result = this.options.onBeforeClose();
      if (result === false) {
        this.isAnimating = false;
        return;
      }
    }

    // Clean up any inline styles from dragging
    if (this.sheet) {
      this.sheet.style.removeProperty('transition');
      this.sheet.style.removeProperty('transform');
    }
    if (this.backdrop) {
      this.backdrop.style.removeProperty('background-color');
    }

    // Remove active class to trigger CSS animations
    this.modal.classList.remove('active');

    // Wait for animation to complete
    await this.wait(this.options.animationDuration);

    // Now hide modal completely
    this.modal.classList.add('hidden');

    // Restore body scroll
    if (this.options.preventScroll) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';

      // Restore scroll position
      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
      }
    }

    this.isOpen = false;
    this.isAnimating = false;

    // Call close callback
    if (this.options.onClose) {
      this.options.onClose();
    }

    // Dispatch custom event
    this.modal.dispatchEvent(
      new CustomEvent('bottomsheet:close', {
        bubbles: true,
        detail: { modalId: this.modalId },
      })
    );
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  setContent(html) {
    if (this.content) {
      this.content.innerHTML = html;
    }
  }

  getContent() {
    return this.content ? this.content.innerHTML : '';
  }

  setTitle(title) {
    const titleElements = this.modal.querySelectorAll('[id$="-title"], [id$="-title-mobile"]');
    titleElements.forEach((el) => {
      el.textContent = title;
    });
  }

  isModalOpen() {
    return this.isOpen;
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  destroy() {
    // Clean up event listeners
    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.close);
    });

    // Restore body scroll if modal was open
    if (this.isOpen && this.options.preventScroll) {
      document.body.style.overflow = '';
    }

    this.isOpen = false;
    this.isAnimating = false;
  }
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.BottomSheetModal = BottomSheetModal;

  // Global modal registry
  window.HeidamanModals = window.HeidamanModals || {};

  /**
   * Global helper function to open any modal by ID
   * Creates modal instance if it doesn't exist yet
   *
   * Usage:
   * openModal('myModalId');
   * openModal('myModalId', { closeOnBackdropClick: false });
   */
  window.openModal = function (modalId, options = {}) {
    // Create modal instance if it doesn't exist
    if (!window.HeidamanModals[modalId]) {
      window.HeidamanModals[modalId] = new BottomSheetModal(modalId, {
        closeOnBackdropClick: true,
        closeOnEscape: true,
        enableSwipeToClose: true,
        swipeThreshold: 100,
        ...options,
      });
    }

    // Open the modal
    window.HeidamanModals[modalId].open();
  };

  /**
   * Global helper function to close any modal by ID
   */
  window.closeModal = function (modalId) {
    if (window.HeidamanModals[modalId]) {
      window.HeidamanModals[modalId].close();
    }
  };

  /**
   * Global helper function to toggle any modal by ID
   */
  window.toggleModal = function (modalId, options = {}) {
    if (!window.HeidamanModals[modalId]) {
      window.HeidamanModals[modalId] = new BottomSheetModal(modalId, {
        closeOnBackdropClick: true,
        closeOnEscape: true,
        enableSwipeToClose: true,
        swipeThreshold: 100,
        ...options,
      });
    }

    window.HeidamanModals[modalId].toggle();
  };

  /**
   * Get modal instance by ID
   */
  window.getModal = function (modalId) {
    return window.HeidamanModals[modalId];
  };
}

window.BottomSheetModal = BottomSheetModal;

}
